import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "redux/rootReducer";
import { Cell } from "react-table";
import { asEur, asPercent } from "utility/numbers";
import { getReportDateSelector } from "redux/user/user";
import { loadAssetClassDistribution } from "redux/assetClassDistribution/assetClassDistributionFunctions";
import { Grid, useMediaQuery } from "@material-ui/core";
import Table from "components/Table";
import { AssetClassDistribution } from "types/AssetClassDistribution";
import { getValueColor } from "utility/textColor";

const AssetClassDistributionList = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const matchesMediaQuery = useMediaQuery("(min-width: 960px)");

  const reportControlId = useSelector((state: RootState) => state.user.reportControlId);
  const reportDate = useSelector(getReportDateSelector);
  const assetClassDistribution = useSelector(
    (state: RootState) => state.assetClassDistribution.all?.data ?? []
  );
  const loading = useSelector((state: RootState) => state.assetClassDistribution.all?.loading);

  const assetClassDistributionGrouped = assetClassDistribution.reduce(
    (result, item) => ({
      ...result,
      [item.Kategoria]: [...(result[item.Kategoria ?? ""] ?? []), item],
    }),
    // tslint:disable-next-line: no-object-literal-type-assertion
    {} as { [key: string]: AssetClassDistribution[] }
  );

  const assetClassDistributionColumns = Object.keys(assetClassDistributionGrouped).map((a) => {
    const total = assetClassDistributionGrouped[a]?.find((acd) => acd.Alakategoria === "Yhteensä");

    return [
      {
        Header: a !== "SALKUT" ? a : "",
        accessor: "Alakategoria",
        paddingRight: "10px",
        minWidth: 170,
        width: 200,
        Footer: total?.Alakategoria ?? "",
      },
      {
        Header: t("weight"),
        accessor: "Paino",
        paddingRight: "10px",
        Cell: (cell: Cell<AssetClassDistribution, number>) =>
          asPercent(cell.value, undefined, true),
        textAlign: "right",
        minWidth: 70,
        width: 70,
        fontFamily: "Lato light",
        Footer: asPercent(total?.Paino, undefined, true),
      },
      {
        Header: t("market_value"),
        accessor: "MarkkinaArvo",
        paddingRight: "10px",
        Cell: (cell: Cell<AssetClassDistribution, number>) => asEur(cell.value, undefined, true),
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: asEur(total?.MarkkinaArvo, undefined, true),
      },
      {
        Header: t("acquisition_value"),
        accessor: "HankintaArvo",
        paddingRight: "10px",
        Cell: (cell: Cell<AssetClassDistribution, number>) =>
          cell.row.original.Kategoria !== "Kassa" ? asEur(cell.value, undefined, true) : "",
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: a !== "Kassa" ? asEur(total?.HankintaArvo, undefined, true) : "",
        hideTitle: a === "Kassa",
      },
      {
        Header: t("dividend_and_other_profits"),
        accessor: "Tuottokassavirta",
        paddingRight: "10px",
        Cell: (cell: Cell<AssetClassDistribution, number>) =>
          cell.row.original.Kategoria !== "Kassa"
            ? getValueColor(asEur(cell.value, undefined, true), cell.value)
            : "",
        textAlign: "right",
        fontFamily: "Lato light",
        Footer:
          a !== "Kassa"
            ? getValueColor(
                asEur(total?.Tuottokassavirta, undefined, true),
                total?.Tuottokassavirta
              )
            : "",
        hideTitle: a === "Kassa",
      },
      {
        Header: t("total_profit"),
        accessor: "TuottoEur",
        paddingRight: "10px",
        Cell: (cell: Cell<AssetClassDistribution, number>) =>
          cell.row.original.Kategoria !== "Kassa"
            ? getValueColor(asEur(cell.value, undefined, true), cell.value)
            : "",
        textAlign: "right",
        fontFamily: "Lato light",
        Footer:
          a !== "Kassa"
            ? getValueColor(asEur(total?.TuottoEur, undefined, true), total?.TuottoEur)
            : "",
        hideTitle: a === "Kassa",
      },
      {
        Header: t("total_profit"),
        accessor: "Tuotto",
        paddingRight: "10px",
        Cell: (cell: Cell<AssetClassDistribution, number>) =>
          cell.row.original.Kategoria !== "Kassa"
            ? getValueColor(asPercent(cell.value, undefined, true), cell.value)
            : "",
        textAlign: "right",
        fontFamily: "Lato light",
        Footer:
          a !== "Kassa"
            ? getValueColor(asPercent(total?.Tuotto, undefined, true), total?.Tuotto)
            : "",
        hideTitle: a === "Kassa",
      },
      {
        Header: t("profit_1m"),
        accessor: "Tuotto1kk",
        whiteSpace: "nowrap",
        paddingRight: "10px",
        Cell: (cell: Cell<AssetClassDistribution, number>) =>
          cell.row.original.Kategoria !== "Kassa"
            ? getValueColor(asPercent(cell.value, undefined, true), cell.value)
            : "",
        textAlign: "right",
        fontFamily: "Lato light",
        Footer:
          a !== "Kassa"
            ? getValueColor(asPercent(total?.Tuotto1kk, undefined, true), total?.Tuotto1kk)
            : "",
        hideTitle: a === "Kassa",
      },
      {
        Header: t("profit_3m"),
        accessor: "Tuotto3kk",
        whiteSpace: "nowrap",
        paddingRight: "10px",
        Cell: (cell: Cell<AssetClassDistribution, number>) =>
          cell.row.original.Kategoria !== "Kassa"
            ? getValueColor(asPercent(cell.value, undefined, true), cell.value)
            : "",
        textAlign: "right",
        fontFamily: "Lato light",
        Footer:
          a !== "Kassa"
            ? getValueColor(asPercent(total?.Tuotto3kk, undefined, true), total?.Tuotto3kk)
            : "",
        hideTitle: a === "Kassa",
      },
      {
        Header: t("profit_ytd"),
        accessor: "TuottoYTD",
        width: 200,
        minWidth: 150,
        Cell: (cell: Cell<AssetClassDistribution, number>) =>
          cell.row.original.Kategoria !== "Kassa"
            ? getValueColor(asPercent(cell.value, undefined, true), cell.value)
            : "",
        textAlign: "right",
        fontFamily: "Lato light",
        Footer:
          a !== "Kassa"
            ? getValueColor(asPercent(total?.TuottoYTD, undefined, true), total?.TuottoYTD)
            : "",
        hideTitle: a === "Kassa",
      },
    ];
  });

  React.useEffect(() => {
    if (reportControlId && reportDate) {
      dispatch(loadAssetClassDistribution(reportControlId, reportDate));
    }
  }, [dispatch, reportControlId, reportDate]);

  return (
    <>
      {Object.keys(assetClassDistributionGrouped).map((a, i) => (
        <Grid
          key={a}
          item
          sm={12}
          style={{
            padding: matchesMediaQuery ? "0 0 16px 0" : "0 16px 16px 16px",
            overflowY: "hidden",
          }}
        >
          <Table
            data={assetClassDistributionGrouped[a].filter((acb) => acb.Alakategoria !== "Yhteensä")}
            columns={assetClassDistributionColumns[i]}
            loading={loading}
            hasFooter
          />
        </Grid>
      ))}
    </>
  );
};

export default React.memo(AssetClassDistributionList);
